import { useFeatureValues, useTeamEntitlements } from "@/query";
import { useQueryParam } from "./useQueryParamHelpers";

// Add/remove feature flags from the arrays below. Enforced via typescript - removing a flag from any of these lists without removing
// its usage will fail CI, and also provide errors pointing to every usage site.

// Query string flags go here. These are useful for shipping dead code (eg., no backend yet) and prototypes.
// These are client side only, not to be confused with database backed feature flags.
// Usage:
//   (url: ?flags=uptime-ratio,some-other-flag)
//   const myFeatureEnabled = useFlag("myFeature");
//   if (myFeatureEnabled) {
//     // do something
//   }
const queryStringFlags = ["uptime-ratio"] as const;

// Database backed feature flags go here. These are useful for shipping features that are not yet ready for all teams.
// These are the feature flags (used in Vendor Portal) that you can toggle for teams in Admin.
export const adminFeatureFlags = [
  "default_to_airgap_docker_registry_format",
  "external-support-bundle",
  "kots_identity_service",
  "kots_license_v1beta2",
  "multiple_apps",
  "ship_vendor_support_analyzers",
  "use-github-issues",
  "allow_disaster_recovery",
  "multi_channel_licenses",
  "license_release_pinning_enabled",
  "allow_snapshots",
  "installer_support_optional",
  "enterprise_portal",
  "enterprise_portal_customer_emails",
  "ep_security_center",
  "ep_webhooks",
  "customizable_install_instructions",
  "publishable_keys",
  "release-validation-v2",
  "enterprise_portal_saml_auth",
  "ep_hide_support_bundle_upload", // for SpecterOps use case only
  "vendor_web_alt",
  "ep_customization_terraform",
  "ep_terraform_modules",
  "cmx_vm_rbac",
  "cmx_overlayfs",
  "allow_classic_notifications",
  "registry_through_proxy_only",
  "download_portal_available"
] as const;

// These feature flags are for features that are not yet ready for all teams, but are not yet ready to be toggled in Admin.
// These can be used to toggle "fake doors" in the UI.
export const futureFeatureFlags = [] as const;

export const useQueryFlag = (flag: (typeof queryStringFlags)[number]) => {
  const [flagsParam] = useQueryParam("flags", []);
  if (flag in queryStringFlags) {
    return flagsParam?.includes(flag) ?? false;
  }
  return false;
};

export const useFeatureFlag = (flag: (typeof adminFeatureFlags)[number]) => {
  const { features = {} } = useFeatureValues();
  if (adminFeatureFlags.includes(flag) && ["0", "1"].includes(features[flag])) {
    return features[flag] === "1";
  }
  if (adminFeatureFlags[flag]) {
    return features[flag];
  }
  return false;
};

/**
 * Like useFeatureFlag but also reports whether the answer can be trusted.
 *
 * `loading` is true while the flags query is in flight. `present` is true only when the
 * key actually came back in the payload: /user/features returns every row of the feature
 * table, not just a team's overrides, so a registered flag is normally present with "0"
 * or "1". A successfully resolved response without the key can occur during a rolling
 * deployment before its migration applies; `error` distinguishes that compatibility case
 * from a request failure. `retry` lets callers offer an explicit recovery path.
 *
 * Use both when rendering a "feature disabled" state that must not appear on a guess.
 */
export const useFeatureFlagStatus = (flag: (typeof adminFeatureFlags)[number]) => {
  const {
    features = {},
    featuresError,
    featuresLoading,
    refetchFeatures
  } = useFeatureValues();
  const present = Object.prototype.hasOwnProperty.call(features, flag);
  let enabled: boolean;
  if (adminFeatureFlags.includes(flag) && ["0", "1"].includes(features[flag])) {
    enabled = features[flag] === "1";
  } else if (adminFeatureFlags[flag]) {
    enabled = Boolean(features[flag]);
  } else {
    enabled = false;
  }
  return {
    enabled,
    error: featuresError,
    loading: featuresLoading,
    present,
    resolved: !featuresLoading && !featuresError,
    retry: refetchFeatures
  };
};

export const useFutureFlag = (flag: (typeof futureFeatureFlags)[number]) => {
  const { futureFeatures = {} } = useFeatureValues();
  if (futureFeatureFlags.includes(flag)) {
    return futureFeatures[flag];
  }
  return false;
};

export const useKurlInstallerAppFlag = () => {
  const kurlInstallerEntitlementEnabled = useTeamEntitlements()?.enable_kurl_installer;
  return kurlInstallerEntitlementEnabled;
};

export const useKotsInstallerAppFlag = () => {
  const kotsInstallerEntitlementEnabled = useTeamEntitlements()?.enable_kots_installer;
  return kotsInstallerEntitlementEnabled;
};
